import { format } from 'date-fns';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from '~/_shared/utils/hooks';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { createSpreadsheetMetricModelForDataActions } from '~/_shared/utils/metric/defaultMetric.helpers';
import { useDefaultMetrics } from '~/_shared/utils/metric/defaultMetric.hook';
import {
  mapMetricsToServerModel, type MetricModel,
} from '~/_shared/utils/metric/metrics.factory';
import { areMetricsTheSame } from '~/_shared/utils/metric/metrics.helpers';
import { MetricsDataAction } from '~/_shared/utils/metric/metrics.types';
import { isTextEmpty } from '~/_shared/utils/text/text.helpers';
import type { ModalProps } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { getUSBoundaryGroupIds } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.helpers';
import { useGetColumnATOMetrics } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/useGetColumnATOMetrics';
import type { CreateOptimizedTerritoriesConfig } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createOptimizedTerritoriesModal/createOptimizedTerritories.types';
import { CreateOptimizedTerritoriesModalComponent } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createOptimizedTerritoriesModal/createOptimizedTerritoriesModal.component';
import { useCreateOptimizedTerritories } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createOptimizedTerritoriesModal/useCreateOptimizedTerritories';
import {
  type CreateOptimizedTerritoriesCountParams, useNumberOfTerritoriesCount,
} from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createOptimizedTerritoriesModal/useNumberOfTerritoriesCount';
import {
  boundaryGroupsSelector, useCustomBoundaryGroupsSelector, useGeneralBoundaryGroups,
  useGetBoundaryGroup,
} from '~/store/boundaryGroups/boundaryGroups.selectors';
import { boundaryCreateOptimizedAddItem } from '~/store/frontendState/mapTools/boundary/boundaryCreateOptimized/boundaryCreateOptimized.actionCreators';
import { INITIAL_BOUNDARY_CREATE_OPTIMIZED_STATUS } from '~/store/frontendState/mapTools/boundary/boundaryCreateOptimized/boundaryCreateOptimized.state';
import {
  boundarySelectEditCloseModal, boundarySelectOptimizedTerritoriesSubsetOpenModal, boundarySelectSetSelectType,
} from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.actionCreators';
import {
  type BoundarySelectState, BoundarySelectType,
} from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.state';
import { useSpreadsheetColumns } from '~/store/matchupData/matchupDataSelectors.hook';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { usePrimarySpreadsheetId } from '~/store/selectors/usePrimarySpreadsheetId';
import { usePrimarySpreadsheetFilterTreeItem } from '~/store/spreadsheetData/filtering/usePrimarySpreadsheetFilterTreeItemRequest';
import {
  BoundaryGroupFromAIModalSteps, BuildingBlockSelection, BuildingBlockSubsetType,
} from '../createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.types';
import { useOptimizedTerritoriesConstraintsValidation } from '../createBoundaryGroupFromAIModal/modalSteps/constraintsValidation/useOptimizedTerrotiriesContraintsValidation';

const createEmptyConfiguration: () => CreateOptimizedTerritoriesConfig = () => ({
  iterations: 2000,
  blockSubset: null,
  aggMetrics: [],
  globalMetrics: [{
    id: 'topological',
    name: 'Regularity',
    weight: OPTIMIZED_TERRITORY_TOPOLOGICAL_WEIGHT_DEFAULT,
    minWeight: OPTIMIZED_TERRITORY_TOPOLOGICAL_WEIGHT_MIN_UI,
    maxWeight: OPTIMIZED_TERRITORY_TOPOLOGICAL_WEIGHT_MAX,
  }],
  previousTerritoriesBoundaryGroupId: null,
  constraints: [],
  btgSettings: null,
  anchorMap: null,
  targetTerritoryCount: null,
  anchorPointsImportance: OPTIMIZED_TERRITORY_AP_IMPORTANCE_DEFAULT,
  excludeDisconnectedBlocks: false,
  exportConfig: null,
  boundaryGroupId: null,
  selectedBoundaryIds: [],

  applyFilterOnData: false,
  buildingBlockSelection: BuildingBlockSelection.ALL,
  buildingBlockSubsetType: BuildingBlockSubsetType.ON_MAP,
  dataMetricsValues: [],
  demographicsValues: [],
  groupName: '',
  numberOfPreviousTerritories: null,
  numberOfSalesReps: null,
  numberOfTerritories: null,
  constraintsEnabled: false,
  overrideTerritory: false,
  previouslySavedBoundaryGroupId: null,
  respectPrevTerritories: false,
  respectSalesLocations: false,
  salesRepLocationsMap: null,
  salesRepNameColumnId: null,
  useNumberOfTerritories: false,
});

export type CreateOptimizedTerritoriesModalProps = ModalProps<{
  initialConfig?: CreateOptimizedTerritoriesConfig;
  initialStep?: BoundaryGroupFromAIModalSteps;
}>;

export const OPTIMIZED_TERRITORY_DEFAULT_TARGET_TERRITORY_COUNT = 2;

// We use a different minimum in the UI to keep the number of values odd so that the default is in the center.
export const OPTIMIZED_TERRITORY_WEIGHT_MIN_UI = 0;
export const OPTIMIZED_TERRITORY_WEIGHT_MIN = 1;
export const OPTIMIZED_TERRITORY_WEIGHT_MAX = 10;
export const OPTIMIZED_TERRITORY_WEIGHT_DEFAULT = 5;

export const OPTIMIZED_TERRITORY_TOPOLOGICAL_WEIGHT_MIN_UI = 0;
export const OPTIMIZED_TERRITORY_TOPOLOGICAL_WEIGHT_MIN = 1;
export const OPTIMIZED_TERRITORY_TOPOLOGICAL_WEIGHT_MAX = 10;
export const OPTIMIZED_TERRITORY_TOPOLOGICAL_WEIGHT_DEFAULT = 5;

export const OPTIMIZED_TERRITORY_AP_IMPORTANCE_MIN_UI = 0;
export const OPTIMIZED_TERRITORY_AP_IMPORTANCE_MIN = 1;
export const OPTIMIZED_TERRITORY_AP_IMPORTANCE_MAX = 10;
export const OPTIMIZED_TERRITORY_AP_IMPORTANCE_DEFAULT = 5;

export const CreateOptimizedTerritoriesModalContainer = ({
  initialConfig,
  initialStep,
  isOpen,
  onClose,
}: CreateOptimizedTerritoriesModalProps) => {
  const [t] = useTranslation();
  const clientId = useClientIdSelector();
  const dispatch = useDispatch();
  const mapId = useMapIdSelector();
  const [currentStep, setCurrentStep] = useState<BoundaryGroupFromAIModalSteps>(initialStep ?? BoundaryGroupFromAIModalSteps.BuildingBlock);
  const [config, setConfig] = useState<CreateOptimizedTerritoriesConfig>({ ...createEmptyConfiguration(), ...initialConfig });
  const [optimizedTerritoriesRequestError, setOptimizedTerritoriesRequestError] = useState<string | null>(null);
  const { openModal: openCreateOptimizedTerritoriesModal } = useModal('CreateOptimizedTerritories');
  const { createOptimizedTerritoriesRequest } = useCreateOptimizedTerritories();
  const spreadsheetId = usePrimarySpreadsheetId();
  const boundariesGroups = useSelector(boundaryGroupsSelector);
  const { getBoundaryGroupById } = useGetBoundaryGroup();
  const { columnMetrics, isLoading: isLoadingColumnMetrics } = useGetColumnATOMetrics(spreadsheetId, { includeGroups: false });
  const spreadsheetColumns = useSpreadsheetColumns();
  const filter = usePrimarySpreadsheetFilterTreeItem();
  const boundariesGroupsZipAndStatesIds = useMemo(() => getUSBoundaryGroupIds(boundariesGroups), [boundariesGroups]);
  const customBoundaryGroups = useCustomBoundaryGroupsSelector();
  const { computeDefaultSpreadsheetColumnMetrics } = useDefaultMetrics();
  const [isGeneratingOptimizedTerritories, setIsGeneratingOptimizedTerritories] = useState(false);
  const [isLoadingSubsetBlocks] = useState(false);
  const globalBoundaryGroups = useGeneralBoundaryGroups();
  const supportedBoundaryGroups = useMemo(() => globalBoundaryGroups.filter(group => group.graphsGenerated), [globalBoundaryGroups]);
  const countOfTerritoriesConfig: CreateOptimizedTerritoriesCountParams = useMemo(() => ({
    numberOfPreviousTerritories: config.numberOfPreviousTerritories,
    numberOfSalesReps: config.numberOfSalesReps,
    previousTerritoriesBoundaryGroupId: config.previousTerritoriesBoundaryGroupId,
    respectPrevTerritories: config.respectPrevTerritories,
    respectSalesLocations: config.respectSalesLocations,
    salesRepLocationsMap: config.salesRepLocationsMap,
    useNumberOfTerritories: config.useNumberOfTerritories,
  }), [config.numberOfPreviousTerritories, config.numberOfSalesReps, config.previousTerritoriesBoundaryGroupId,
    config.respectPrevTerritories, config.respectSalesLocations, config.salesRepLocationsMap, config.useNumberOfTerritories]);

  const constraintValidationState = useOptimizedTerritoriesConstraintsValidation({
    boundaryGroupId: config.boundaryGroupId,
    constraintsEnabled: config.constraintsEnabled,
    demographicsValues: config.demographicsValues,
    dataMetricsValues: config.dataMetricsValues,
    blockSubset: config.buildingBlockSelection === BuildingBlockSelection.SUBSET ? config.selectedBoundaryIds : undefined,
    filter: config.applyFilterOnData ? filter : undefined,
    excludeDisconnectedBlocks: config.excludeDisconnectedBlocks,
  });

  const onNumberOfSalesRepChange = useCallback((numberOfSalesReps: number | null) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      numberOfSalesReps,
    }));
  }, []);

  const onNumberOfTerritoriesChange = useCallback((numberOfTerritories: number | null) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      numberOfTerritories,
    }));
  }, []);

  const {
    numberOfTerritoriesUpdateReason, onRefreshTerritoriesCount, handleNumberOfTerritoriesChange, numberOfTerritoriesLoading,
  } = useNumberOfTerritoriesCount({ config: countOfTerritoriesConfig, onNumberOfTerritoriesChange, onNumberOfSalesRepChange });

  const onGoToStep = useCallback((step: BoundaryGroupFromAIModalSteps) => {
    setCurrentStep(step);
  }, []);

  useEffect(() => {
    setConfig(previous => ({
      ...previous,
      dataMetricsValues: columnMetrics.map(({ columnId, name, numericPercent }) => ({
        id: columnId,
        name,
        checked: false,
        weight: OPTIMIZED_TERRITORY_WEIGHT_DEFAULT,
        isConstraint: false,
        numericPercent,
      })),
    }));
  }, [columnMetrics]);

  useEffect(() => {
    if (!config.boundaryGroupId) {
      return;
    }

    const boundaryGroup = getBoundaryGroupById(config.boundaryGroupId);

    setConfig(previous => ({
      ...previous,
      demographicsValues: (boundaryGroup?.demographics ?? []).map(demographic => ({
        id: demographic.id,
        name: demographic.name,
        weight: OPTIMIZED_TERRITORY_WEIGHT_DEFAULT,
        checked: false,
        isConstraint: false,
      })),
    }));
  }, [boundariesGroups, boundariesGroupsZipAndStatesIds.statesGroupId, boundariesGroupsZipAndStatesIds.zipsGroupId, config.boundaryGroupId, getBoundaryGroupById, t]);

  const onSelectSubsetFromMap = useCallback(() => {
    if (!config.boundaryGroupId) {
      return;
    }

    onClose();
    dispatch(boundarySelectSetSelectType(BoundarySelectType.Lasso));

    dispatch(boundarySelectOptimizedTerritoriesSubsetOpenModal({
      boundaryGroupId: config.boundaryGroupId,
      selectedBoundaryIds: config.selectedBoundaryIds,
      configuration: config,
      onClose: () => {
        openCreateOptimizedTerritoriesModal({
          initialConfig: config,
          initialStep: BoundaryGroupFromAIModalSteps.BuildingBlock,
        });
        dispatch(boundarySelectEditCloseModal());
      },
      onSubmit: (boundarySelect: BoundarySelectState) => {
        if (!boundarySelect?.selectedBoundaryInfos?.length) {
          return;
        }

        openCreateOptimizedTerritoriesModal({
          initialConfig: {
            ...config,
            selectedBoundaryIds: boundarySelect.selectedBoundaryInfos.map(boundaryInfo => boundaryInfo.boundaryId),
          },
          initialStep: BoundaryGroupFromAIModalSteps.BuildingBlock,
        });
        dispatch(boundarySelectEditCloseModal());
      },
    }));
  }, [config, dispatch, onClose, openCreateOptimizedTerritoriesModal]);

  const onChange = useCallback((update: Partial<CreateOptimizedTerritoriesConfig>) => {
    setOptimizedTerritoriesRequestError(null);
    setConfig(previous => ({
      ...previous,
      ...update,
    }));

    handleNumberOfTerritoriesChange(update);
  }, [handleNumberOfTerritoriesChange]);

  const onCloseOptimizedTerritoriesRequestError = useCallback(() => {
    setOptimizedTerritoriesRequestError(null);
  }, []);

  const onSubmit = useCallback(async () => {
    if (clientId && mapId && spreadsheetId && config.boundaryGroupId) {
      setOptimizedTerritoriesRequestError(null);
      setIsGeneratingOptimizedTerritories(true);
      try {
        const columnIds = new Set(spreadsheetColumns.map((column) => column.id));
        const defaultSpreadsheetColumnMetrics = await computeDefaultSpreadsheetColumnMetrics();

        const creationParametersMetrics: MetricModel[] = config.dataMetricsValues
          .filter(metric => metric.checked && columnIds.has(metric.id))
          .flatMap((metric) => {
            return createSpreadsheetMetricModelForDataActions(
              metric.id,
              spreadsheetId,
              metric.name,
              [MetricsDataAction.HIGH_VALUE, MetricsDataAction.LOW_VALUE, MetricsDataAction.SUM, MetricsDataAction.AVERAGE]
            );
          });

        const filteredDefaultMetric = [...defaultSpreadsheetColumnMetrics].filter((defaultMetric) => {
          return !creationParametersMetrics.some((creationMetric) => areMetricsTheSame(defaultMetric, creationMetric));
        });

        const combinedMetrics = [...creationParametersMetrics, ...filteredDefaultMetric];

        const serverModelMetrics = mapMetricsToServerModel(combinedMetrics);

        const boundaryGroup = supportedBoundaryGroups.find(group => group.id === config.boundaryGroupId);
        const formattedDate = format(new Date(), 'MM/dd/yyyy, HH:mm:ss');

        const response = await createOptimizedTerritoriesRequest({
          clientId,
          mapId,
          spreadsheetId,
          blockSubset: config.selectedBoundaryIds.length > 0 ? config.selectedBoundaryIds : undefined,
          excludeDisconnectedBlocks: config.selectedBoundaryIds.length > 0 ? false : config.excludeDisconnectedBlocks,
          boundaryGroupId: config.boundaryGroupId,
          targetTerritoryCount: config.numberOfTerritories,
          btgSettings: {
            metrics: serverModelMetrics,
          },
          previousTerritoriesBoundaryGroupId: config.previousTerritoriesBoundaryGroupId ?? undefined,
          anchorPointsImportance: config.anchorPointsImportance,
          exportConfig: config.exportConfig ?? undefined,
          anchorsMap: (!!config.salesRepLocationsMap && !!config.salesRepNameColumnId) ? {
            mapId: config.salesRepLocationsMap.mapId,
            nameColumnId: config.salesRepNameColumnId,
          } : undefined,
          groupName: isTextEmpty(config.groupName) ? `Territories from ${boundaryGroup?.name ?? ''} (${formattedDate})` : config.groupName,
          filter: config.applyFilterOnData && filter ? { filter_tree: filter?.filterTree } : undefined,
          useConstraints: config.constraintsEnabled,
          iterations: config.iterations,
          dataMetricsValues: config.dataMetricsValues,
          demographicsValues: config.demographicsValues,
          globalMetrics: config.globalMetrics,
        });

        if (response.hash) {
          dispatch(boundaryCreateOptimizedAddItem({
            type: 'Maptive',
            isFailed: false,
            hash: response.hash,
            status: INITIAL_BOUNDARY_CREATE_OPTIMIZED_STATUS,
            startedOnCurrentTab: true,
          }));
        }

        onClose();
      }
      catch (e) {
        setOptimizedTerritoriesRequestError(e.message);
      }
      finally {
        setIsGeneratingOptimizedTerritories(false);
      }
    }
  }, [clientId, mapId, spreadsheetId, config, spreadsheetColumns, computeDefaultSpreadsheetColumnMetrics,
    supportedBoundaryGroups, createOptimizedTerritoriesRequest, filter, onClose, dispatch]);

  return (
    <CreateOptimizedTerritoriesModalComponent
      config={config}
      constraintValidationState={constraintValidationState}
      currentStep={currentStep}
      customBoundaryGroups={customBoundaryGroups}
      supportedBoundaryGroups={supportedBoundaryGroups}
      isGenerateOptimizedTerritoriesLoading={isGeneratingOptimizedTerritories}
      isLoadingDataMetrics={isLoadingColumnMetrics}
      isLoadingSubsetBlocks={isLoadingSubsetBlocks}
      isOpen={isOpen}
      numberOfTerritoriesLoading={numberOfTerritoriesLoading}
      numberOfTerritoriesUpdateReason={numberOfTerritoriesUpdateReason}
      onChange={onChange}
      onClose={onClose}
      onCloseOptimizedTerritoriesRequestError={onCloseOptimizedTerritoriesRequestError}
      onGoToStep={onGoToStep}
      onRefreshTerritoriesCount={onRefreshTerritoriesCount}
      onSelectSubsetFromMap={onSelectSubsetFromMap}
      onSubmit={onSubmit}
      optimizedTerritoriesRequestError={optimizedTerritoriesRequestError}
      markerFilterActive={!!filter}
      minWeight={OPTIMIZED_TERRITORY_WEIGHT_MIN_UI}
      maxWeight={OPTIMIZED_TERRITORY_WEIGHT_MAX}
    />
  );
};
